import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    DialogProps,
    SxProps,
    Theme
} from '@mui/material';
import { darken } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { useBusinessTheme } from '../../contexts/BusinessThemeContext';

interface BaseDialogProps extends Omit<DialogProps, 'title'> {
    open: boolean;
    onClose: () => void;
    title: string | React.ReactNode;
    children: React.ReactNode;
    actions?: React.ReactNode;
    fullWidth?: boolean;
    fullScreen?: boolean;
    maxWidth?: DialogProps['maxWidth'];
    contentSx?: SxProps<Theme>;
    actionsSx?: SxProps<Theme>;
    titleSx?: SxProps<Theme>;
    showCloseButton?: boolean;
    titleActions?: React.ReactNode;
}

const BaseDialog: React.FC<BaseDialogProps> = ({
    open,
    onClose,
    title,
    children,
    actions,
    fullWidth = true,
    fullScreen,
    maxWidth,
    contentSx,
    actionsSx,
    titleSx,
    showCloseButton = true,
    titleActions,
    sx,
    ...dialogProps
}) => {
    const { theme: businessTheme } = useBusinessTheme();
    
    // Default responsive dialog sizing
    const defaultSx: SxProps<Theme> = {
        '& .MuiDialog-paper': {
            margin: { xs: '5px', sm: '32px' },
            maxHeight: { xs: 'calc(100vh - 10px)', sm: 'calc(100vh - 64px)' },
            maxWidth: !maxWidth 
                ? { xs: 'calc(100vw - 10px)', sm: '600px' }
                : undefined,
            width: !maxWidth 
                ? { xs: 'calc(100vw - 10px)', sm: 'auto' }
                : undefined
        }
    };

    // Merge default sx with provided sx
    const mergedSx: SxProps<Theme> = sx 
        ? { ...defaultSx, ...(typeof sx === 'object' ? sx : {}) }
        : defaultSx;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth={fullWidth}
            fullScreen={fullScreen}
            maxWidth={maxWidth}
            sx={mergedSx}
            {...dialogProps}
        >
            <DialogTitle sx={{ 
                ...titleSx,
                backgroundColor: businessTheme?.primaryColor || '#1976d2',
                color: businessTheme?.secondaryColor ||'#ffffffE6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '& .MuiIconButton-root': {
                    color: businessTheme?.secondaryColor ||'#ffffffE6'
                }
            }}>
                {typeof title === 'string' ? title : title}
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {titleActions}
                    {showCloseButton && (
                        <IconButton
                            onClick={onClose}
                            sx={{ color: businessTheme?.secondaryColor ||'#ffffffE6' }}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>
                    )}
                </div>
            </DialogTitle>
            <DialogContent sx={{
                position: 'relative',
                overflowY: 'auto',
                scrollbarGutter: 'stable',
                pb: 2,
                px: { xs: '5px', sm: 3 },
                '&::-webkit-scrollbar': {
                    width: '8px'
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#bbb',
                    borderRadius: '4px',
                    '&:hover': {
                        background: '#999'
                    }
                },
                ...contentSx
            }}>
                {children}
            </DialogContent>
            {actions && (
                <DialogActions sx={{ 
                    p: { xs: 1, sm: 2 }, 
                    gap: 1,
                    bgcolor: businessTheme?.backgroundColor,
                    backgroundColor: businessTheme?.backgroundColor 
                        ? darken(businessTheme.backgroundColor, 0.03)
                        : darken('#ffffff', 0.03),
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    ...actionsSx 
                }}>
                    {actions}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default BaseDialog;
